import { useEntitiesServiceEntitiesVehiclesListKey } from '@insurance/api/queries'
import { EntitiesService } from '@insurance/api/requests'
import { CheckboxCardGroup, RadioCardGroup } from '@insurance/components'
import { ConditionalText } from '@shared/components'
import { useDebounce, useIntersectionObserver } from '@shared/hooks'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Flex, Input, Modal, ModalProps, Spin } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStyles } from './styles'
import { CommonType } from './types'

type Props<M extends boolean> = {
  onCancel?: () => void
} & CommonType<M> &
  ModalProps

const LARGE_PAGE_SIZE = 50

export function VehicleModalSelect<M extends boolean>({
  multiple,
  value: externValue,
  vehicleType,
  onAdd,
  getTitle,
  getSubtitle,
  getExtra,
  ...props
}: Props<M>) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const { styles } = useStyles()
  const [value, setValue] = useState<CommonType<M>['value']>(externValue)

  const debouncedSearch = useDebounce(search)

  useEffect(() => {
    setValue(externValue)
  }, [externValue])

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useEntitiesServiceEntitiesVehiclesListKey,
      debouncedSearch,
      vehicleType
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await EntitiesService.entitiesVehiclesList(
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        pageParam,
        LARGE_PAGE_SIZE,
        undefined,
        debouncedSearch,
        vehicleType,
        undefined,
        undefined
      )

      return {
        data: response?.results,
        total_pages: Math.ceil((response?.count || 0) / LARGE_PAGE_SIZE),
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: CommonType<true>['value']
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1,
    enabled: !!props.open
  })

  const { observerElement } = useIntersectionObserver({
    onIntersect: fetchNextPage,
    enabled: !!props.open
  })

  const vehicles = data?.pages.flatMap((page) => page.data)

  const handleOk = () => {
    onAdd?.(value)

    props.onCancel?.()
  }

  const checkboxValues = value as CommonType<true>['value']
  const setCheckboxValues = setValue as React.Dispatch<
    React.SetStateAction<CommonType<true>['value']>
  >

  const radioValue = value as CommonType<false>['value']
  const setRadioValue = setValue as React.Dispatch<
    React.SetStateAction<CommonType<false>['value']>
  >

  const componentTitle = (vehicle: CommonType<false>['value']) => {
    if (!vehicle) return null

    if (getTitle) return getTitle(vehicle)

    return (
      <ConditionalText
        condition={!![vehicle.brand, vehicle.model].filter(Boolean).length}
        strong
      >
        {[vehicle.brand, vehicle.model].filter(Boolean).join(' ')}
      </ConditionalText>
    )
  }

  const componentSubtitle = (vehicle: CommonType<false>['value']) => {
    if (!vehicle) return null

    if (getSubtitle) return getSubtitle(vehicle)

    return (
      <ConditionalText condition={!!vehicle.registration_number} strong>
        {vehicle.registration_number}
      </ConditionalText>
    )
  }

  const componentExtra = (vehicle: CommonType<false>['value']) => {
    if (!vehicle) return null

    if (getExtra) return getExtra(vehicle)

    return null
  }

  return (
    <Modal title={t('vehicles')} onOk={handleOk} {...props}>
      <Flex vertical gap={12}>
        <Input
          placeholder={t('search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Flex vertical gap={12} className={styles.content}>
          {multiple ? (
            <CheckboxCardGroup
              value={checkboxValues?.map(({ id }) => id)}
              options={vehicles?.map((vehicle) => ({
                label: (
                  <Flex vertical>
                    {componentTitle(vehicle)}
                    {componentSubtitle(vehicle)}
                    {componentExtra(vehicle)}
                  </Flex>
                ),
                value: vehicle?.id,
                colProps: { xs: 24, lg: 12 },
                onChange: () =>
                  setCheckboxValues((values) => [
                    ...(values?.filter(({ id }) => id !== vehicle?.id) || []),
                    ...(vehicle && !values?.some(({ id }) => id === vehicle?.id)
                      ? [vehicle]
                      : [])
                  ])
              }))}
            />
          ) : (
            <RadioCardGroup
              value={radioValue?.id}
              options={vehicles?.map((vehicle) => ({
                label: (
                  <Flex vertical>
                    {componentTitle(vehicle)}
                    {componentSubtitle(vehicle)}
                    {componentExtra(vehicle)}
                  </Flex>
                ),
                value: vehicle?.id,
                colProps: { xs: 24, lg: 12 },
                onChange: () => setRadioValue(vehicle)
              }))}
            />
          )}

          {isFetching && (
            <Flex justify="center">
              <Spin spinning />
            </Flex>
          )}

          {observerElement}
        </Flex>
      </Flex>
    </Modal>
  )
}
